<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {ElCheckboxButton, ElCheckboxGroup, ElCollapse, ElCollapseItem, ElSwitch, ElTable, ElTableColumn} from 'element-plus';
import {CabPeriod, FINANCE_THEMES, FinancienPage, formatGetal, formatMln, formatPercentage, formatSignedPercentage, KLEUR_CEL, Pivot, StatusKleur, ThemaKolom} from 'common';
import {dashboardFinancien} from '@/base/callBackend.js';
import {dashboardInclPrognose, dashboardOrgs, dashboardPeriod, dashboardThemas, laadDashboard} from '@/base/dashboardState.js';
import KpiCard from '@/views/dashboard/KpiCard.vue';
import UitgavenOverzicht from '@/views/dashboard/UitgavenOverzicht.vue';
import WaterfallChart from '@/views/dashboard/WaterfallChart.vue';

const loading = ref<boolean>(true);
const page    = ref<FinancienPage | undefined>(undefined);
const open    = ref<string[]>([]);
let loadSeq: number = 0;

const pivotOrgRijen   = computed<Record<string, string>[]>(() => page.value === undefined ? [] : page.value.pivotOrganisatie.rijen.map((_, i) => pivotRij(page.value!.pivotOrganisatie, i)));
const pivotThemaRijen = computed<Record<string, string>[]>(() => page.value === undefined ? [] : page.value.pivotThema.rijen.map((_, i) => pivotRij(page.value!.pivotThema, i)));

watch([dashboardPeriod, dashboardOrgs, dashboardThemas, dashboardInclPrognose], load, {immediate: true});

async function load() {
    if (dashboardPeriod.value === '') {
        return;
    }
    const seq: number                       = ++loadSeq;
    loading.value                           = true;
    const result: FinancienPage | undefined = await laadDashboard(() => dashboardFinancien(dashboardPeriod.value, dashboardOrgs.value, dashboardThemas.value, dashboardInclPrognose.value));
    if (seq !== loadSeq) {
        return;
    }
    page.value    = result;
    loading.value = false;
}

function kwartaalKort(kwartaal: string, volgend: boolean): string {
    const p: CabPeriod = CabPeriod.ofString(kwartaal);
    return (volgend ? p.nextPeriod() : p).toString().split('-')[1];
}

function achtergrond(kleur: StatusKleur): string {
    return kleur === StatusKleur.grijs ? '' : KLEUR_CEL[kleur];
}

function miljoen(v: number): string {
    return Math.abs(v) < 0.005 ? '' : formatGetal(v, 2);
}

function pivotRij(p: Pivot, i: number): Record<string, string> {
    const rij: Record<string, string> = {label: p.rijen[i].label, totaal: miljoen(p.rijen[i].totaal)};
    p.kolommen.forEach((k, j) => rij[k] = miljoen(p.rijen[i].waarden[j]));
    return rij;
}

function themaRijen(kolommen: ThemaKolom[], volgend: string): Record<string, string>[] {
    const rij = (label: string, f: (k: ThemaKolom) => string): Record<string, string> => {
        const r: Record<string, string> = {label};
        kolommen.forEach(k => r[k.label] = f(k));
        return r;
    };
    return [
        rij('IZA-budget', k => formatMln(k.beschikbaar)),
        rij(`Prognose t/m ${volgend}`, k => formatMln(k.prognose)),
        rij('Verschil', k => formatMln(k.verschil)),
        rij('%', k => formatPercentage(k.percentage)),
    ];
}
</script>

<template>
    <div class="px-10 py-4 flex flex-col gap-8">
        <div class="flex flex-row flex-wrap items-center gap-4">
            <span class="font-thin">Thema</span>
            <el-checkbox-group v-model="dashboardThemas" class="flex">
                <el-checkbox-button v-for="t in FINANCE_THEMES" :key="t.label" :value="t.label" border>{{ t.label }}</el-checkbox-button>
            </el-checkbox-group>
            <span v-if="dashboardThemas.length === 0" class="text-xs text-gray-500">alle thema's</span>
        </div>

        <div v-if="loading" class="text-sm text-gray-500">laden...</div>
        <template v-else-if="page">
            <section class="flex flex-col gap-2">
                <div class="text-xl font-semibold text-zzc-blue">Samenvatting</div>
                <el-table :data="page.samenvatting" size="small">
                    <el-table-column prop="label" label="KPI" min-width="220"/>
                    <el-table-column :label="`Status ${dashboardPeriod}`" width="220">
                        <template #default="scope">
                            <div class="flex justify-between px-2 py-1 rounded" :style="{backgroundColor: achtergrond(scope.row.kleur)}">
                                <span>{{ formatMln(scope.row.waarde) }}</span><span>{{ formatSignedPercentage(scope.row.voortgang) }}</span>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Target" width="130"><template #default="scope">{{ scope.row.target === undefined ? 'n.v.t.' : formatMln(scope.row.target) }}</template></el-table-column>
                    <el-table-column prop="definitie" label="Definitie target" min-width="320"/>
                </el-table>
            </section>

            <section class="flex flex-col gap-3">
                <div class="flex flex-row items-center gap-4">
                    <div class="text-xl font-semibold text-zzc-blue">Gerealiseerde en geprognosticeerde kosten</div>
                    <el-switch v-model="dashboardInclPrognose" active-text="incl. prognose Q+1"/>
                </div>
                <div class="flex flex-row flex-wrap gap-4">
                    <KpiCard label="Beschikbaar" :value="formatMln(page.uitgaven.beschikbaar)"/>
                    <KpiCard :label="`Gerealiseerd t/m ${kwartaalKort(dashboardPeriod, false)}`" :value="formatMln(page.uitgaven.gerealiseerd)"/>
                    <KpiCard :label="`Prognose ${kwartaalKort(dashboardPeriod, true)}`" :value="formatMln(page.uitgaven.prognose)"/>
                    <KpiCard label="Resterend" :value="formatMln(page.uitgaven.resterend)" :detail="formatPercentage(page.uitgaven.uitputting)"/>
                </div>
                <UitgavenOverzicht :uitgaven="page.uitgaven" :incl-prognose="dashboardInclPrognose"/>
            </section>

            <section class="flex flex-col gap-2">
                <div class="text-xl font-semibold text-zzc-blue">Toegekend IZA-budget per status</div>
                <WaterfallChart :waterfall="page.waterfall"/>
            </section>

            <el-collapse v-model="open">
                <el-collapse-item name="themas" title="Geprognosticeerde uitgaven per thema">
                    <el-table :data="themaRijen(page.themaMatrix.kolommen, page.themaMatrix.volgendKwartaal)" size="small">
                        <el-table-column prop="label" label="KPI" width="200"/>
                        <el-table-column v-for="k in page.themaMatrix.kolommen" :key="k.label" :prop="k.label" :label="k.label" min-width="110" align="right"/>
                    </el-table>
                    <div class="font-semibold mt-4 mb-1">Ondersteuningsorganisatie (OO) - uitsplitsing</div>
                    <el-table :data="themaRijen(page.themaMatrix.ooKolommen, page.themaMatrix.volgendKwartaal)" size="small">
                        <el-table-column prop="label" label="KPI" width="200"/>
                        <el-table-column v-for="k in page.themaMatrix.ooKolommen" :key="k.label" :prop="k.label" :label="k.label" min-width="110" align="right"/>
                    </el-table>
                    <div class="text-xs text-gray-500 mt-2">Totaal is de optelsom van de IZA-begroting van alle thema's t/m de eerstvolgende tranche. F&C = Finance en Control, Participatie = Inwonerparticipatie, ZDC = Zeeuwse Datacoalitie.</div>
                </el-collapse-item>
                <el-collapse-item name="pivots" title="Toegekend budget per organisatie en per thema (in EUR miljoenen)">
                    <div class="font-semibold mb-1">Cumulatief sinds 2023, voor alle thema's van de transformatie fase 1</div>
                    <el-table :data="pivotOrgRijen" size="small">
                        <el-table-column prop="label" label="Toegekend status" width="240"/>
                        <el-table-column v-for="k in page.pivotOrganisatie.kolommen" :key="k" :prop="k" :label="k" min-width="100" align="right"/>
                        <el-table-column prop="totaal" label="Totaal" width="110" align="right"/>
                    </el-table>
                    <div class="font-semibold mt-4 mb-1">Cumulatief sinds 2023, voor alle onderdelen van het thema Ondersteuningsorganisatie (OO)</div>
                    <el-table :data="pivotThemaRijen" size="small">
                        <el-table-column prop="label" label="Toegekend status" width="240"/>
                        <el-table-column v-for="k in page.pivotThema.kolommen" :key="k" :prop="k" :label="k" min-width="100" align="right"/>
                        <el-table-column prop="totaal" label="Totaal" width="110" align="right"/>
                    </el-table>
                </el-collapse-item>
            </el-collapse>
        </template>
        <div v-else class="text-sm text-gray-500">Geen gegevens (laden mislukt).</div>
    </div>
</template>
